import React, { useState, useEffect, useMemo } from 'react';
import { Layout, Menu, Button, Dropdown, Avatar, Space, Badge, Drawer, Tooltip } from 'antd';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
    MenuFoldOutlined,
    MenuUnfoldOutlined,
    LogoutOutlined,
    MenuOutlined,
    CloseOutlined,
    ShoppingCartOutlined,
    WalletOutlined,
    SunOutlined,
    MoonOutlined,
    SearchOutlined,
    GlobalOutlined,
    AppstoreOutlined,
    EditOutlined,
} from '@ant-design/icons';
import { useAuth } from '../../shared/hooks/useAuth';
import { useTheme } from '../../theme/ThemeContext';
import { billingService } from '../services/billingService';
import { cartService } from '../services/cartService';
import AppBreadcrumb from '../../shared/components/AppBreadcrumb';
import CommandPalette from '../../shared/components/CommandPalette';
import BrandLogo from '../../shared/components/BrandLogo';
import BalanceMoney, { balanceOriginalText } from '../../shared/components/BalanceMoney';
import { NAV_SECTIONS, ACCOUNT_MENU, SETTINGS_PAGE, TRANSFER_PAGE, PALETTE_COMMANDS, resolveRoute } from '../navigation';
import transferService from '../services/transferService';
import couponService from '../services/couponService';
import onboardingService from '../services/onboardingService';
import CouponTicketModal from '../components/CouponTicket';
import MenuEditModal from '../components/MenuEditModal';
import { productIdOf, cachedMenu, loadMenu, saveMenu, shownIds } from '../menuPrefs';
import { useTranslation, LANGUAGES, changeLanguage } from '../../shared/i18n';
import CountryFlag, { LANGUAGE_FLAGS } from '../../shared/components/CountryFlag';

import { userPath } from '../../shared/config/paths';
import ErrorBoundary from '../../shared/components/ErrorBoundary';
import NotificationBell from '../components/NotificationBell';
import { PLATFORM_CURRENCY } from '../../shared/utils/money';

const { Header, Sider, Content } = Layout;

const UserLayout = ({ children }) => {
    const { t, i18n } = useTranslation();
    // 获取窗口宽度来确定是否是移动设备
    const [windowWidth, setWindowWidth] = useState(window.innerWidth);
    // 从localStorage获取折叠状态，默认为折叠状态
    const [collapsed, setCollapsed] = useState(localStorage.getItem('userSidebarCollapsed') === 'true');
    // 新增抽屉菜单状态
    const [drawerVisible, setDrawerVisible] = useState(false);
    // 余额状态
    const [balance, setBalance] = useState(0);
    const [balanceCurrency, setBalanceCurrency] = useState(PLATFORM_CURRENCY);
    const [balanceView, setBalanceView] = useState(null);
    // 购物车商品数量
    const [cartItemCount, setCartItemCount] = useState(0);
    // 待我接受的资源转移数
    const [pendingTransfers, setPendingTransfers] = useState(0);
    // Cmd+K 命令面板
    const [paletteOpen, setPaletteOpen] = useState(false);

    const location = useLocation();
    const navigate = useNavigate();
    const { currentUser, logout } = useAuth();
    const { isDark, toggleTheme } = useTheme();

    // 左侧菜单:默认只显示已开通的产品,其余收进「更多产品」;用户可自定义(user/menuPrefs.js)
    const [menuState, setMenuState] = useState(() => cachedMenu());
    const [menuEditOpen, setMenuEditOpen] = useState(false);
    const [menuSaving, setMenuSaving] = useState(false);
    useEffect(() => {
        loadMenu().then(setMenuState).catch(() => {});
    }, []);
    const saveMenuPrefs = async (shown) => {
        setMenuSaving(true);
        try {
            setMenuState(await saveMenu(shown, menuState));
            setMenuEditOpen(false);
        } catch (e) { /* apiClient shows the error */ } finally {
            setMenuSaving(false);
        }
    };

    // 监听窗口尺寸变化
    useEffect(() => {
        const handleResize = () => {
            setWindowWidth(window.innerWidth);
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // 全局快捷键：Cmd/Ctrl+K 打开命令面板，Esc 关闭
    useEffect(() => {
        const handleHotkey = (e) => {
            const isMac = navigator.platform.toLowerCase().includes('mac');
            const modifier = isMac ? e.metaKey : e.ctrlKey;
            if (modifier && (e.key === 'k' || e.key === 'K')) {
                e.preventDefault();
                setPaletteOpen(p => !p);
            } else if (e.key === 'Escape' && paletteOpen) {
                setPaletteOpen(false);
            }
        };
        window.addEventListener('keydown', handleHotkey);
        return () => window.removeEventListener('keydown', handleHotkey);
    }, [paletteOpen]);

    // 加载用户余额和购物车
    useEffect(() => {
        const loadBalance = async () => {
            try {
                const response = await billingService.getBalance();
                if (response.success && response.data) {
                    setBalance(response.data.balance);
                    if (response.data.currency) setBalanceCurrency(response.data.currency);
                    setBalanceView(response.data);
                }
            } catch (error) {
                console.error('Failed to load balance:', error);
            }
        };

        const loadCartCount = async () => {
            try {
                const response = await cartService.getCart();
                if (response.success && response.data) {
                    setCartItemCount(response.data.total_items || 0);
                }
            } catch (error) {
                console.error('Failed to load cart:', error);
            }
        };

        if (currentUser) {
            loadBalance();
            loadCartCount();
        }
        // 资源页「续费」加入购物车后即时刷新角标
        const onCart = () => { if (currentUser) loadCartCount(); };
        window.addEventListener('cart:updated', onCart);
        return () => window.removeEventListener('cart:updated', onCart);
    }, [currentUser, location.pathname]); // 路径变化时重新加载购物车数量

    // 待接受的资源转移（头像角标 + 账户菜单计数）
    useEffect(() => {
        if (!currentUser) return undefined;
        const load = () => transferService.pendingCount().then((d) => setPendingTransfers(d?.pending_in || 0)).catch(() => {});
        load();
        window.addEventListener('transfers:changed', load);
        return () => window.removeEventListener('transfers:changed', load);
    }, [currentUser, location.pathname]);

    // 7 天内到期的可用优惠券数（账户菜单「我的优惠券」角标）
    const [couponsExpiring, setCouponsExpiring] = useState(0);
    // 新到的券（没见过）→ 券面弹窗；首登欢迎弹窗还没看过时由首页的欢迎弹窗带券面（两个弹窗不叠）
    const [ticketsOn, setTicketsOn] = useState(false);
    useEffect(() => {
        if (!currentUser) return;
        couponService.summary().then((r) => {
            setCouponsExpiring(r?.success ? (r.data?.expiring_soon || 0) : 0);
            if (r?.success && r.data?.unseen > 0) {
                onboardingService.get().then((o) => setTicketsOn(!o?.welcome)).catch(() => setTicketsOn(true));
            }
        }).catch(() => {});
    }, [currentUser]);

    // 判断是否是移动设备
    const isMobile = windowWidth < 768;

    // 菜单 / 面包屑 / 高亮 全部来自 user/navigation.jsx 同一张路由表
    const route = useMemo(() => resolveRoute(location.pathname), [location.pathname]);
    const selectedKeys = route.leaf ? [route.leaf.path] : [];
    const routeOpenKey = route.leaf?.sub && route.leaf.sub.key !== ACCOUNT_MENU.key ? route.leaf.sub.key : null;
    const routeProduct = route.leaf ? productIdOf(route.leaf.sub || route.leaf) : null;
    const [openKeys, setOpenKeys] = useState(routeOpenKey ? [routeOpenKey] : []);
    useEffect(() => {
        if (routeOpenKey) setOpenKeys((keys) => (keys.includes(routeOpenKey) ? keys : [...keys, routeOpenKey]));
    }, [routeOpenKey]);
    useEffect(() => {
        const ids = shownIds(menuState);
        if (routeProduct && ids && !ids.includes(routeProduct)) {
            setOpenKeys((keys) => (keys.includes('more-products') ? keys : [...keys, 'more-products']));
        }
    }, [routeProduct, menuState]);

    // 切换折叠状态
    const toggleCollapsed = () => {
        if (isMobile) {
            // 在移动端，控制抽屉菜单
            setDrawerVisible(!drawerVisible);
        } else {
            // 在桌面端，控制侧边栏折叠
            const newCollapsed = !collapsed;
            setCollapsed(newCollapsed);
            localStorage.setItem('userSidebarCollapsed', newCollapsed.toString());
        }
    };

    const handleLogout = async () => {
        await logout();
    };

    const leafItem = (l) => ({
        key: l.path || l.href,
        icon: l.icon,
        label: l.href
            ? <a href={l.href} target="_blank" rel="noopener noreferrer">{t(l.label)}</a>
            : <Link to={userPath(l.path)}>{t(l.label)}</Link>,
    });

    const productItem = (item) => {
        if (!item.children) return leafItem(item);
        const kids = item.children.filter((c) => !c.requires || menuState?.[c.requires]);
        return kids.length === 1
            ? leafItem({ ...kids[0], label: item.label, icon: item.icon })
            : { key: item.key, icon: item.icon, label: t(item.label), children: kids.map(leafItem) };
    };
    const shown = shownIds(menuState); // null = menu not loaded yet → show everything
    const isShown = (item) => {
        const id = productIdOf(item);
        return !id || !shown || shown.includes(id);
    };
    const rank = (item) => {
        const id = productIdOf(item);
        return id && shown ? shown.indexOf(id) : -1;
    };
    const hiddenProducts = [];
    const menuItems = NAV_SECTIONS.map((section) => {
        section.items.forEach((item) => { if (!isShown(item)) hiddenProducts.push(item); });
        const items = section.items.filter(isShown).sort((a, b) => rank(a) - rank(b)).map(productItem);
        if (!items.length) return [];
        return section.title
            ? { type: 'group', key: `sec-${section.key}`, label: t(section.title), children: items }
            : items;
    }).flat();
    // 「工单」等固定项放在最后的分组里;更多产品 + 编辑菜单插在它前面
    const tail = menuItems.length && menuItems[menuItems.length - 1].key === 'sec-support' ? [menuItems.pop()] : [];
    if (hiddenProducts.length) {
        menuItems.push({
            key: 'more-products', icon: <AppstoreOutlined />, label: t('More products'),
            children: hiddenProducts.map(productItem),
        });
    }
    menuItems.push(...tail);
    menuItems.push({ key: 'edit-menu', icon: <EditOutlined />, label: t('Edit menu') });

    // 右上角账户菜单：费用中心 + 个人设置 + 退出（参考阿里云/AWS）
    const accountMenu = {
        items: [
            {
                key: 'who',
                disabled: true,
                label: (
                    <div style={{ lineHeight: 1.4, padding: '2px 0' }}>
                        <div style={{ fontWeight: 600, color: 'var(--text-1)' }}>{currentUser?.username || t('User')}</div>
                        {currentUser?.email && <div style={{ fontSize: 12, color: 'var(--text-3)' }}>{currentUser.email}</div>}
                    </div>
                ),
            },
            { type: 'divider' },
            {
                type: 'group',
                key: 'billing-group',
                label: t(ACCOUNT_MENU.label),
                children: ACCOUNT_MENU.children.map((c) => ({
                    key: c.path,
                    label: c.path === '/billing/coupons' && couponsExpiring > 0
                        ? <Badge count={couponsExpiring} size="small" offset={[10, 0]}>{t(c.label)}</Badge>
                        : t(c.label),
                })),
            },
            { type: 'divider' },
            { key: SETTINGS_PAGE.path, icon: SETTINGS_PAGE.icon, label: t(SETTINGS_PAGE.label) },
            {
                key: TRANSFER_PAGE.path,
                icon: TRANSFER_PAGE.icon,
                label: <Badge count={pendingTransfers} size="small" offset={[10, 0]}>{t(TRANSFER_PAGE.label)}</Badge>,
            },
            {
                key: 'lang',
                icon: <GlobalOutlined />,
                // language names always in their own language (endonyms), never translated
                label: t('Language') + ' · ' + (LANGUAGES.find((l) => l.code === i18n.language)?.label || '简体中文'),
                children: LANGUAGES.map((l) => ({ key: `lang:${l.code}`, icon: <CountryFlag code={LANGUAGE_FLAGS[l.code]} />, label: l.label })),
            },
            { key: 'logout', icon: <LogoutOutlined />, label: t('Sign out'), danger: true },
        ],
        selectedKeys,
        onClick: ({ key }) => {
            if (key.startsWith('lang:')) { changeLanguage(key.slice(5)); return; }
            if (key === 'logout') handleLogout();
            else if (key.startsWith('/')) navigate(userPath(key));
        },
    };

    const initials = (currentUser?.username || currentUser?.email || '?').trim().charAt(0).toUpperCase();

    const sideMenu = (onClick) => (
        <Menu
            mode="inline"
            theme={isDark ? 'dark' : 'light'}
            selectedKeys={selectedKeys}
            openKeys={collapsed && !isMobile ? undefined : openKeys}
            onOpenChange={setOpenKeys}
            items={menuItems}
            onClick={(info) => {
                if (info.key === 'edit-menu') { setMenuEditOpen(true); return; }
                if (onClick) onClick(info);
            }}
            className="user-side-menu"
            style={{ borderRight: 0, background: 'transparent' }}
        />
    );

    return (
        <Layout style={{ minHeight: '100vh' }}>
            {/* 桌面端侧边栏 */}
            {!isMobile && (
                <Sider
                    trigger={null}
                    collapsible
                    collapsed={collapsed}
                    breakpoint="lg"
                    width={232}
                    collapsedWidth={72}
                    theme={isDark ? 'dark' : 'light'}
                    className="app-sider"
                    style={{
                        overflow: 'auto',
                        height: '100vh',
                        position: 'sticky',
                        top: 0,
                        left: 0,
                        zIndex: 1000,
                    }}
                >
                    <Link
                        to={userPath('/dashboard')}
                        className="app-sider-logo"
                        style={{ justifyContent: collapsed ? 'center' : 'flex-start', padding: collapsed ? 0 : '0 20px' }}
                        aria-label={t('Yeap Cloud console')}
                    >
                        <BrandLogo collapsed={collapsed} />
                    </Link>
                    {sideMenu()}
                </Sider>
            )}

            {/* 移动端抽屉菜单 */}
            {isMobile && (
                <Drawer
                    title={<BrandLogo size={24} />}
                    placement="left"
                    onClose={() => setDrawerVisible(false)}
                    open={drawerVisible}
                    styles={{ body: { padding: 0 } }}
                    width={264}
                    style={{ zIndex: 1001 }}
                    className="mobile-drawer user-mobile-drawer"
                    closeIcon={<CloseOutlined />}
                >
                    {sideMenu(() => setDrawerVisible(false))}
                </Drawer>
            )}

            <Layout>
                <Header
                    className="app-header"
                    style={{
                        padding: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        position: 'sticky',
                        top: 0,
                        zIndex: 999,
                    }}
                >
                    <Button
                        type="text"
                        aria-label={isMobile ? t('Open menu') : (collapsed ? t('Expand sidebar') : t('Collapse sidebar'))}
                        icon={isMobile ? <MenuOutlined /> : (collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />)}
                        onClick={toggleCollapsed}
                        style={{ fontSize: 16, width: 56, height: 56 }}
                    />

                    {isMobile && (
                        <Link to={userPath('/dashboard')} style={{ flex: 1, display: 'flex', justifyContent: 'center', minWidth: 0 }}>
                            <BrandLogo size={24} />
                        </Link>
                    )}

                    <Space size={isMobile ? 4 : 12} style={{ marginRight: isMobile ? 8 : 16, flexShrink: 0 }}>
                        {!isMobile && (
                            <Tooltip title={t('Search (Ctrl/⌘+K)')}>
                                <div
                                    onClick={() => setPaletteOpen(true)}
                                    onKeyDown={(e) => { if (e.key === 'Enter') setPaletteOpen(true); }}
                                    className="cmdk-trigger"
                                    role="button"
                                    tabIndex={0}
                                >
                                    <SearchOutlined style={{ fontSize: 14 }} />
                                    <span style={{ flex: 1 }}>{t('Search products and pages…')}</span>
                                    <kbd className="cmdk-kbd">⌘K</kbd>
                                </div>
                            </Tooltip>
                        )}
                        {isMobile && (
                            <Button type="text" shape="circle" aria-label={t('Search')} icon={<SearchOutlined />} onClick={() => setPaletteOpen(true)} />
                        )}
                        {!isMobile && (
                            <Tooltip title={balanceOriginalText(balanceView) ? `${t('Balance')} · ${balanceOriginalText(balanceView)}` : t('Balance')}>
                                <button
                                    type="button"
                                    className="header-balance"
                                    onClick={() => navigate(userPath('/billing/balance'))}
                                >
                                    <WalletOutlined />
                                    <BalanceMoney view={balanceView || { balance, currency: balanceCurrency }} noTooltip showApprox={false} />
                                </button>
                            </Tooltip>
                        )}
                        <Tooltip title={cartItemCount > 0 ? t('Cart ({{cartItemCount}})', { cartItemCount }) : t('Cart')}>
                            <Badge count={cartItemCount} size="small" showZero={false} offset={[-2, 2]}>
                                <Button
                                    type="text"
                                    shape="circle"
                                    aria-label={t('Cart')}
                                    icon={<ShoppingCartOutlined />}
                                    onClick={() => navigate(userPath('/billing/cart'))}
                                />
                            </Badge>
                        </Tooltip>
                        <NotificationBell />
                        <Tooltip title={isDark ? t('Switch to light mode') : t('Switch to dark mode')}>
                            <Button
                                type="text"
                                shape="circle"
                                aria-label={isDark ? t('Switch to light mode') : t('Switch to dark mode')}
                                icon={isDark ? <SunOutlined /> : <MoonOutlined />}
                                onClick={toggleTheme}
                                className="theme-toggle-btn"
                            />
                        </Tooltip>
                        <Dropdown menu={accountMenu} trigger={['click']} placement="bottomRight">
                            <Badge dot={pendingTransfers > 0} offset={[-4, 4]}>
                            <Avatar
                                src={currentUser?.avatar_url || undefined}
                                style={{ cursor: 'pointer', background: 'var(--brand)', flexShrink: 0 }}
                                className="header-avatar"
                                role="button"
                                aria-label={t('Account menu')}
                            >
                                {initials}
                            </Avatar>
                            </Badge>
                        </Dropdown>
                    </Space>
                </Header>
                <Content
                    className="page-content"
                    style={{
                        margin: 0,
                        padding: isMobile ? '12px 12px 24px' : '16px 24px 32px',
                        minHeight: 280,
                        // 不能用 overflow:auto —— 它让 Content 成为滚动容器(却不滚),页面内 position:sticky 全部失效
                        minWidth: 0,
                        background: 'transparent',
                    }}
                >
                    <AppBreadcrumb crumbs={route.crumbs} />
                    <div className="page-content-inner">
                        <ErrorBoundary resetKeys={[location.pathname]}>{children}</ErrorBoundary>
                    </div>
                </Content>
            </Layout>
            <CommandPalette open={paletteOpen} onClose={() => setPaletteOpen(false)} commands={PALETTE_COMMANDS} />
            <CouponTicketModal enabled={ticketsOn} />
            <MenuEditModal
                open={menuEditOpen}
                shown={shownIds(menuState)}
                saving={menuSaving}
                onCancel={() => setMenuEditOpen(false)}
                onSave={saveMenuPrefs}
                onReset={() => saveMenuPrefs(null)}
            />
        </Layout>
    );
};

export default UserLayout;
